import { inject, Injectable } from "@angular/core";
import { TemplateService } from "@domain/project/template/template.service";
import { BusinessScope } from "@lib/constants/business-scope";
import { aggregateSupportedLocales, getScopeLocales, ScopeLocales } from "@lib/utils/template-locale-aggregator";
import { catchError, defer, forkJoin, map, Observable, of, shareReplay } from "rxjs";

@Injectable({ providedIn: "root" })
export class TemplateLocaleService {
  private templateService = inject(TemplateService);

  private readonly globalTemplates$ = defer(() =>
    forkJoin({
      fgds: this.templateService.getGlobalTemplate(BusinessScope.FGDS).pipe(catchError(() => of(undefined))),
      monitoring: this.templateService.getGlobalTemplate(BusinessScope.MONITORING).pipe(catchError(() => of(undefined))),
    }),
  ).pipe(shareReplay(1));

  readonly allSupportedCountryCodes$: Observable<string[]> = this.globalTemplates$.pipe(
    map(({ fgds, monitoring }) => aggregateSupportedLocales(fgds, monitoring).countryCodes),
  );

  readonly allSupportedLanguageCodes$: Observable<string[]> = this.globalTemplates$.pipe(
    map(({ fgds, monitoring }) => aggregateSupportedLocales(fgds, monitoring).languageCodes),
  );

  getCountryCodesByScope(scope: BusinessScope | undefined): Observable<string[]> {
    if (!scope) {
      return this.allSupportedCountryCodes$;
    }
    return this.globalTemplates$.pipe(
      map(({ fgds, monitoring }) => {
        const locales = getScopeLocales(fgds, monitoring);
        return scope === BusinessScope.FGDS ? locales.fgds.countryCodes : locales.monitoring.countryCodes;
      }),
    );
  }

  getLanguageCodesByScope(scope: BusinessScope | undefined): Observable<string[]> {
    if (!scope) {
      return this.allSupportedLanguageCodes$;
    }
    return this.globalTemplates$.pipe(
      map(({ fgds, monitoring }) => {
        const locales = getScopeLocales(fgds, monitoring);
        return scope === BusinessScope.FGDS ? locales.fgds.languageCodes : locales.monitoring.languageCodes;
      }),
    );
  }

  getAllScopeLocaleCodes(): Observable<ScopeLocales> {
    return this.globalTemplates$.pipe(map(({ fgds, monitoring }) => getScopeLocales(fgds, monitoring)));
  }
}
